import type {
  Cadence,
  HeroKpiVisibility,
  MockSectionCustomerInput,
  ReportCustomerDto,
  ReportSectionDto,
  SectionMetaDto,
} from '@act/shared/data-transfer-objects';
import { normalizeHeroKpiVisibility } from '@act/shared/data-transfer-objects';
import type { PreviewSection } from '../utils/reportDisplay.util';
import { resolveSectionSnapshot, toMockSectionCustomerInput } from '../utils/resolveSectionSnapshot.util';

/** Reading-column row shape expected by hosted Studio section components. */
export interface ReportReadingColumnItem {
  sectionId: string;
  order: number;
  included: boolean;
  calloutEnabled: boolean;
  narrative: string | null;
  callout: string | null;
  calloutTitle: string | null;
  clinicalInsightEnabled: boolean;
  financialInsightEnabled: boolean;
  heroKpiVisibility: HeroKpiVisibility;
  insights: ReportSectionDto['insights'];
  dataSnapshot: Record<string, unknown> | null;
}

/**
 * Maps API customer to mock snapshot customer input.
 * @param customer - Reports customer DTO.
 * @returns Mock generator input.
 */
export const toPreviewCustomerInput = (
  customer: ReportCustomerDto,
): MockSectionCustomerInput => toMockSectionCustomerInput(customer);

/**
 * Maps a preview section union to the reading column item shape.
 * @param section - Draft or published preview section.
 * @returns Item for {@link ReportReadingColumn}.
 */
export const toReadingColumnItem = (
  section: PreviewSection,
): ReportReadingColumnItem => ({
  sectionId: section.sectionId,
  order: section.order,
  included: 'included' in section ? section.included : true,
  calloutEnabled: section.calloutEnabled,
  narrative: section.narrative,
  callout: section.callout,
  calloutTitle: 'calloutTitle' in section ? section.calloutTitle : null,
  clinicalInsightEnabled:
    'clinicalInsightEnabled' in section ? section.clinicalInsightEnabled : true,
  financialInsightEnabled:
    'financialInsightEnabled' in section ? section.financialInsightEnabled : true,
  heroKpiVisibility: normalizeHeroKpiVisibility(
    'heroKpiVisibility' in section ? section.heroKpiVisibility : undefined,
  ),
  insights: 'insights' in section ? (section.insights ?? null) : null,
  dataSnapshot:
    'dataSnapshot' in section ? section.dataSnapshot : null,
});

/**
 * Reading column item with resolved snapshot JSON (mock or stored).
 * @param section - Preview section row.
 * @param customer - Customer DTO.
 * @param reportingPeriod - Report period label.
 * @returns Item with `dataSnapshot` populated for renderers.
 */
export const toReadingColumnItemWithSnapshot = (
  section: PreviewSection,
  customer: ReportCustomerDto,
  reportingPeriod: string,
  measurementPeriod?: string,
): ReportReadingColumnItem => {
  const stored =
    'dataSnapshot' in section ? section.dataSnapshot : null;
  const resolvedMeasurementPeriod = measurementPeriod ?? reportingPeriod;
  return {
    ...toReadingColumnItem(section),
    dataSnapshot: resolveSectionSnapshot({
      sectionId: section.sectionId,
      dataSnapshot: stored,
      customer,
      reportingPeriod,
      measurementPeriod: resolvedMeasurementPeriod,
    }),
  };
};

/**
 * Resolves section library metadata for the renderer.
 * @param meta - Library metadata from shared DTOs.
 * @returns Metadata with `id` for Studio renderer compatibility.
 */
export const sectionMetaForRenderer = (
  meta: SectionMetaDto,
): SectionMetaDto => meta;

/** Normalizes report cadence for reading column props. */
export const normalizeReportCadence = (cadence: string): Cadence => {
  if (cadence === 'quarterly' || cadence === 'adhoc') {
    return cadence;
  }
  return 'monthly';
};
